/**
 * Ürünün ihtiyaç duyduğu beş grafik. Hepsi Grafik kabuğunun içinde yaşar ve
 * doğrulanmış paleti kullanır.
 *
 * Form seçimleri (dataviz "choosing-a-form"):
 *  · En çok alınan 5 ürün  -> sıralanmış YATAY ÇUBUK. Büyüklük + uzun etiket.
 *  · Aylık harcama trendi  -> ÇİZGİ. Zaman içinde değişim, tek seri.
 *  · Kâr-zarar             -> gelir/gider ÇUBUK + net KUTUPLAŞMA çizgisi. Tek eksen.
 *  · Saatlik yoğunluk      -> ÇUBUK. 24 kategori, tek seri, seçici etiket.
 *  · Nokta karşılaştırma   -> gruplu ÇUBUK. Kategorik kimlik, efsane zorunlu.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { paraGoster } from "@kantin/shared";
import { Grafik, Ipucu } from "./Grafik.js";
import { GRAFIK_RENK, MARK, renkAtayici, siraliRenk } from "./palet.js";

/**
 * ADR-03 KURALININ TEK VE AÇIK İSTİSNASI.
 *
 * Recharts piksel konumu hesaplamak için `number` ister; SVG koordinatı
 * float'tır, başka türlüsü mümkün değil. Burada yapılan şey ARİTMETİK DEĞİL,
 * yalnızca ÇİZİM: gelen değer sunucuda Decimal ile hesaplanmış ve
 * döndürülmüş bir string'dir; hiçbir toplama/çarpma bu sayılar üzerinden
 * yapılmaz ve bu sayı asla geri yazılmaz.
 *
 * Kullanıcının GÖRDÜĞÜ tüm tutarlar (etiket, ipucu, tablo) her zaman
 * orijinal string'den paraGoster() ile üretilir — aşağıdaki sayıdan değil.
 * Kural bu tek fonksiyonda, gerekçesiyle birlikte devre dışı bırakılır.
 */
// eslint-disable-next-line no-restricted-syntax
const cizimDegeri = (paraStr: string): number => Number(paraStr);

const eksenStili = { fill: "var(--gr-eksen)", fontSize: 12 };
const izgara = { stroke: GRAFIK_RENK.izgara, strokeDasharray: "0" };

// ---------------------------------------------------------------------------
// 1 · En çok alınan 5 ürün (KK-706) — sıralanmış yatay çubuk, TEK seri
// ---------------------------------------------------------------------------

export interface UrunSirasi {
  urun_ad: string;
  adet: number;
  tutar: string;
}

export function EnCokAlinanUrunler({
  veri,
  yukleniyor,
}: {
  veri: UrunSirasi[];
  yukleniyor?: boolean;
}) {
  const sirali = [...veri]
    .sort((a, b) => cizimDegeri(b.tutar) - cizimDegeri(a.tutar))
    .slice(0, 5);
  const enBuyuk = sirali.length ? cizimDegeri(sirali[0]!.tutar) : 1;

  return (
    <Grafik
      baslik="En çok alınan ürünler"
      altBaslik="Son 30 gün · tutara göre"
      ozet={`Son 30 günde en çok harcanan ${sirali.length} ürün listeleniyor.`}
      tabloBasliklari={["Ürün", "Adet", "Tutar"]}
      tabloSatirlari={sirali.map((u) => [
        u.urun_ad,
        u.adet,
        paraGoster(u.tutar),
      ])}
      yukleniyor={yukleniyor ?? false}
    >
      {/* Tek seri: efsane YOK, bunun yerine her çubukta doğrudan etiket. */}
      <ResponsiveContainer
        width="100%"
        height={Math.max(160, sirali.length * 44)}
      >
        <BarChart
          data={sirali.map((u) => ({ ...u, _deger: cizimDegeri(u.tutar) }))}
          layout="vertical"
          margin={{ top: 4, right: 64, bottom: 4, left: 4 }}
          barCategoryGap={MARK.bosluk * 4}
        >
          <CartesianGrid {...izgara} horizontal={false} />
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="urun_ad"
            width={130}
            tick={eksenStili}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<Ipucu />} cursor={{ fill: "var(--uzeri)" }} />
          <Bar
            dataKey="_deger"
            name="Tutar"
            radius={MARK.cubukKoseYatay}
            maxBarSize={22}
          >
            {sirali.map((u, i) => (
              <Cell
                key={u.urun_ad}
                fill={siraliRenk(1 - i / Math.max(1, sirali.length))}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ul className="mt-2 flex flex-col gap-1 text-sm text-murekkep-2">
        {sirali.map((u) => (
          <li key={u.urun_ad} className="flex list-none justify-between gap-4">
            <span>{u.urun_ad}</span>
            <span className="rakam font-mono text-murekkep">
              {u.adet} adet · {paraGoster(u.tutar)}
              <span className="ml-2 text-xs text-murekkep-3">
                %{Math.round((cizimDegeri(u.tutar) / (enBuyuk || 1)) * 100)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Grafik>
  );
}

// ---------------------------------------------------------------------------
// 2 · Aylık harcama trendi (KK-706) — çizgi, TEK seri
// ---------------------------------------------------------------------------

export interface AylikNokta {
  ay: string;
  tutar: string;
}

export function HarcamaTrendi({
  veri,
  yukleniyor,
}: {
  veri: AylikNokta[];
  yukleniyor?: boolean;
}) {
  return (
    <Grafik
      baslik="Aylık harcama"
      altBaslik="Son 6 ay"
      ozet="Aylık toplam kantin harcamasının zaman içindeki değişimi."
      tabloBasliklari={["Ay", "Harcama"]}
      tabloSatirlari={veri.map((v) => [v.ay, paraGoster(v.tutar)])}
      yukleniyor={yukleniyor ?? false}
    >
      <ResponsiveContainer width="100%" height={220}>
        <LineChart
          data={veri.map((v) => ({ ...v, _deger: cizimDegeri(v.tutar) }))}
          margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
        >
          <CartesianGrid {...izgara} vertical={false} />
          <XAxis
            dataKey="ay"
            tick={eksenStili}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={eksenStili}
            axisLine={false}
            tickLine={false}
            width={56}
          />
          <Tooltip
            content={<Ipucu />}
            cursor={{ stroke: GRAFIK_RENK.eksen, strokeWidth: 1 }}
          />
          {/* Trend BİLEŞİK bir işarettir: giren/çıkan yön konvansiyonu
              tek tek tutarlar içindir, buraya uygulanmaz (bkz. palet.ts). */}
          <Line
            type="monotone"
            dataKey="_deger"
            name="Harcama"
            stroke={GRAFIK_RENK.marka}
            strokeWidth={MARK.cizgiKalinlik}
            dot={{
              r: MARK.isaretciYaricap,
              fill: GRAFIK_RENK.marka,
              strokeWidth: 0,
            }}
            activeDot={{ r: MARK.isaretciYaricap + 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Grafik>
  );
}

// ---------------------------------------------------------------------------
// 3 · Kâr-zarar (KK-605, KK-609) — gelir/gider çubuk + net kutuplaşma
// ---------------------------------------------------------------------------

export interface KarZararNoktasi {
  donem: string;
  gelir: string;
  gider: string;
  net: string;
}

export function KarZarar({
  veri,
  yukleniyor,
}: {
  veri: KarZararNoktasi[];
  yukleniyor?: boolean;
}) {
  const d = veri.map((v) => ({
    ...v,
    _gelir: cizimDegeri(v.gelir),
    _gider: cizimDegeri(v.gider),
    _net: cizimDegeri(v.net),
  }));

  return (
    <Grafik
      baslik="Kâr-zarar"
      altBaslik="Gelir, mal maliyeti + giderler ve net"
      ozet="Dönem bazında gelir, gider ve net kâr-zarar. Net çizgisi sıfırın altına indiğinde zarar demektir."
      efsane={[
        { anahtar: "gelir", etiket: "Gelir", renk: GRAFIK_RENK.kategorik[0] },
        { anahtar: "gider", etiket: "Gider", renk: GRAFIK_RENK.kategorik[1] },
        { anahtar: "net", etiket: "Net", renk: GRAFIK_RENK.arti },
      ]}
      tabloBasliklari={["Dönem", "Gelir", "Gider", "Net"]}
      tabloSatirlari={veri.map((v) => [
        v.donem,
        paraGoster(v.gelir),
        paraGoster(v.gider),
        paraGoster(v.net),
      ])}
      yukleniyor={yukleniyor ?? false}
    >
      {/* TEK EKSEN: gelir, gider ve net aynı para biriminde. Çift eksen YOK. */}
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart
          data={d}
          margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
        >
          <CartesianGrid {...izgara} vertical={false} />
          <XAxis
            dataKey="donem"
            tick={eksenStili}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={eksenStili}
            axisLine={false}
            tickLine={false}
            width={64}
          />
          <Tooltip content={<Ipucu />} cursor={{ fill: "var(--uzeri)" }} />
          <ReferenceLine y={0} stroke={GRAFIK_RENK.eksen} strokeWidth={1} />
          <Bar
            dataKey="_gelir"
            name="Gelir"
            fill={GRAFIK_RENK.kategorik[0]}
            radius={MARK.cubukKose}
            maxBarSize={28}
          />
          <Bar
            dataKey="_gider"
            name="Gider"
            fill={GRAFIK_RENK.kategorik[1]}
            radius={MARK.cubukKose}
            maxBarSize={28}
          />
          <Line
            type="monotone"
            dataKey="_net"
            name="Net"
            strokeWidth={MARK.cizgiKalinlik}
            stroke={GRAFIK_RENK.arti}
            dot={(props: {
              cx?: number;
              cy?: number;
              payload?: { _net?: number };
              index?: number;
            }) => {
              const eksi = (props.payload?._net ?? 0) < 0;
              return (
                <circle
                  key={props.index}
                  cx={props.cx}
                  cy={props.cy}
                  r={MARK.isaretciYaricap}
                  fill={eksi ? GRAFIK_RENK.eksi : GRAFIK_RENK.arti}
                  stroke={GRAFIK_RENK.yuzey}
                  strokeWidth={MARK.bosluk}
                />
              );
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Grafik>
  );
}

// ---------------------------------------------------------------------------
// 4 · Saatlik yoğunluk (KK-803) — teneffüs pikleri, TEK seri
// ---------------------------------------------------------------------------

export interface SaatlikNokta {
  saat: string;
  islem: number;
}

export function SaatlikYogunluk({
  veri,
  yukleniyor,
}: {
  veri: SaatlikNokta[];
  yukleniyor?: boolean;
}) {
  const enYuksek = veri.reduce((m, v) => Math.max(m, v.islem), 0) || 1;
  return (
    <Grafik
      baslik="Saatlik yoğunluk"
      altBaslik="Teneffüs pikleri · personel planlaması için"
      ozet="Gün içindeki saatlik işlem adedi. Yüksek çubuklar teneffüs pikleridir."
      tabloBasliklari={["Saat", "İşlem"]}
      tabloSatirlari={veri.map((v) => [v.saat, v.islem])}
      yukleniyor={yukleniyor ?? false}
    >
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={veri}
          margin={{ top: 8, right: 8, bottom: 4, left: 4 }}
          barCategoryGap={2}
        >
          <CartesianGrid {...izgara} vertical={false} />
          <XAxis
            dataKey="saat"
            tick={eksenStili}
            axisLine={false}
            tickLine={false}
            interval={1}
          />
          {/* İşlem adedi tam sayıdır: 0.5 / 1.5 gibi kesirli eksen adımı olmaz. */}
          <YAxis
            tick={eksenStili}
            axisLine={false}
            tickLine={false}
            width={40}
            allowDecimals={false}
          />
          <Tooltip
            content={<Ipucu birim="işlem" />}
            cursor={{ fill: "var(--uzeri)" }}
          />
          <Bar dataKey="islem" name="İşlem" radius={MARK.cubukKose}>
            {/* Büyüklük tek hueli rampayla kodlanır — pik kendiliğinden öne çıkar. */}
            {veri.map((v) => (
              <Cell key={v.saat} fill={siraliRenk(v.islem / enYuksek)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Grafik>
  );
}

// ---------------------------------------------------------------------------
// 5 · Nokta karşılaştırma (KK-810) — kategorik kimlik, efsane ZORUNLU
// ---------------------------------------------------------------------------

export interface NoktaOlcumu {
  nokta_id: string;
  nokta_ad: string;
  ciro: string;
  sepet_ortalama: string;
  islem: number;
}

export function NoktaKarsilastirma({
  veri,
  yukleniyor,
}: {
  veri: NoktaOlcumu[];
  yukleniyor?: boolean;
}) {
  // Renk VARLIĞA sabitlenir: bir nokta filtrelenirse kalanların rengi değişmez.
  const renk = renkAtayici(veri.map((v) => v.nokta_id));
  const gosterilen = veri.slice(0, 5);
  const digerSayisi = veri.length - gosterilen.length;

  return (
    <Grafik
      baslik="Noktalar karşılaştırması"
      altBaslik="Ciro · sepet ortalaması · işlem adedi"
      ozet="Okuldaki satış noktalarının ciro karşılaştırması."
      efsane={gosterilen.map((v) => ({
        anahtar: v.nokta_id,
        etiket: v.nokta_ad,
        renk: renk(v.nokta_id),
      }))}
      tabloBasliklari={["Nokta", "Ciro", "Sepet ort.", "İşlem"]}
      tabloSatirlari={veri.map((v) => [
        v.nokta_ad,
        paraGoster(v.ciro),
        paraGoster(v.sepet_ortalama),
        v.islem,
      ])}
      yukleniyor={yukleniyor ?? false}
    >
      <ResponsiveContainer
        width="100%"
        height={Math.max(180, gosterilen.length * 52)}
      >
        <BarChart
          data={gosterilen.map((v) => ({ ...v, _ciro: cizimDegeri(v.ciro) }))}
          layout="vertical"
          margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
          barCategoryGap={MARK.bosluk * 4}
        >
          <CartesianGrid {...izgara} horizontal={false} />
          <XAxis
            type="number"
            tick={eksenStili}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="nokta_ad"
            width={150}
            tick={eksenStili}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<Ipucu />} cursor={{ fill: "var(--uzeri)" }} />
          <Bar
            dataKey="_ciro"
            name="Ciro"
            radius={MARK.cubukKoseYatay}
            maxBarSize={26}
          >
            {gosterilen.map((v) => (
              <Cell key={v.nokta_id} fill={renk(v.nokta_id)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {digerSayisi > 0 && (
        <p className="mt-2 text-xs text-murekkep-3">
          {digerSayisi} nokta daha var; grafikte ilk 5 gösteriliyor — tam liste
          için tablo görünümünü açın.
        </p>
      )}
    </Grafik>
  );
}
